import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    fetch('https://readdy.ai/api/form/d9q7j7me3oq4jqumthjg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(
        Array.from(formData.entries()).map(([k, v]) => [k, String(v)]),
      ).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed: any;
        try { parsed = JSON.parse(text); } catch { parsed = {}; }
        if (res.ok && parsed?.code === 'OK') {
          setFormStatus('success');
          form.reset();
        } else {
          const msg = parsed?.meta?.message || parsed?.message || text;
          if (String(msg).includes('spam') || String(msg).includes('form data is spam')) {
            setFormError(msg);
            setFormStatus('error');
          } else {
            setFormError(msg || '提交失败，请稍后重试');
            setFormStatus('error');
          }
        }
      })
      .catch(() => {
        setFormError('网络错误，请检查连接后重试');
        setFormStatus('error');
      });
  };

  const stats = [
    { value: '50+', label: '深度文章' },
    { value: '5', label: '核心分类' },
    { value: '10+', label: '覆盖司法管辖区' },
    { value: '100%', label: '专业合规导向' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="pt-24 pb-16 md:pb-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-5">
              关于我们
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              我们是一个专注于成人内容行业合规运营的专业知识平台。我们的使命是为全球从业者提供权威、及时、实用的合规指南，帮助行业在合法合规的框架下健康发展。
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 bg-background-50 rounded-xl border border-background-200">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
              我们的理念
            </h2>
            <div className="space-y-5 text-sm text-foreground-700 leading-relaxed">
              <p>
                成人内容行业是一个在全球范围内广泛存在且持续增长的产业。然而，由于文化禁忌和法律复杂性，行业从业者往往面临信息不对称、合规资源匮乏的困境。合规运营指南应运而生，致力于填补这一信息鸿沟。
              </p>
              <p>
                我们相信，合规不是业务的障碍，而是可持续发展的基石。通过建立完善的合规体系，成人内容平台不仅能够规避法律风险，还能在用户信任、商业合作和品牌建设方面获得长期竞争优势。
              </p>
              <p>
                我们的内容团队由法律专业人士、技术架构师和行业运营专家组成，确保每一篇文章都兼具专业深度和实操价值。我们持续追踪全球主要司法管辖区的法律法规更新，为读者提供最及时的合规资讯。
              </p>
            </div>
          </div>
        </section>

        <section id="contact" className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-10">
              如果您有任何合规咨询需求或合作意向，欢迎通过以下方式联系我们。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="bg-background-50 rounded-xl p-6 border border-background-200">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-1 text-sm">电子邮箱</h3>
                <p className="text-sm text-foreground-600">contact@compliance-guide.example.com</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-1 text-sm">响应时间</h3>
                <p className="text-sm text-foreground-600">我们将在1-2个工作日内回复您的咨询</p>
              </div>
            </div>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200"
            >
              <h3 className="font-semibold text-foreground-900 mb-6 text-base">发送消息</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  placeholder="咨询主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  消息内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                  placeholder="请输入您的消息（最多500个字符）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="sr-only"
              />

              <button
                type="submit"
                className="w-full sm:w-auto px-7 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors duration-200"
              >
                发送消息
              </button>

              {formStatus === 'success' && (
                <p className="mt-4 text-sm text-green-600">消息已成功发送，感谢您的联系！</p>
              )}
              {formStatus === 'error' && (
                <p className="mt-4 text-sm text-red-600">{formError || '发送失败，请稍后重试'}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}